import { useEffect, useRef, useState } from 'react';

export default function Hero() {
  const heroRef = useRef<HTMLDivElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const [videoFailed, setVideoFailed] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!heroRef.current || !bgRef.current) return;
      // Only parallax on desktop (>= 768px)
      if (window.innerWidth < 768) {
        bgRef.current.style.transform = '';
        return;
      }
      const scrollY = window.scrollY;
      const heroHeight = heroRef.current.offsetHeight;
      if (scrollY <= heroHeight) {
        const ratio = scrollY / heroHeight;
        bgRef.current.style.transform = `translateY(${ratio * 120}px) scale(${1 + ratio * 0.08})`;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section ref={heroRef} id="top" className="relative h-[100svh] min-h-[640px] w-full overflow-hidden" aria-label="ヒーローエリア">
      {/* Parallax background layer */}
      <div ref={bgRef} className="absolute inset-0 -top-[10%] -bottom-[10%] will-change-transform">
        {/* Video */}
        <div className={`absolute inset-0 ${videoFailed ? 'hidden' : ''}`}>
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster="https://readdy.ai/api/search-image?query=Warm%20modern%20Japanese%20apartment%20living%20room%20after%20professional%20interior%20renovation%2C%20sparkling%20clean%20polished%20light%20wooden%20floor%20reflecting%20soft%20golden%20afternoon%20sunlight%20through%20large%20windows%2C%20fresh%20cream%20beige%20walls%20with%20subtle%20smooth%20texture%2C%20minimalist%20elegant%20interior%20design%2C%20spacious%20empty%20room%20ready%20for%20new%20tenant%20move-in%2C%20warm%20neutral%20earth%20tones%2C%20no%20people%2C%20high-end%20architectural%20interior%20photography%2C%20serene%20and%20inviting%20atmosphere%2C%20natural%20diffused%20daylight%2C%20pristine%20move-in%20condition%2C%20soft%20shadows%20across%20the%20floor&width=1600&height=900&seq=hero-poster-wind-2026-v2&orientation=landscape"
            className="w-full h-full object-cover object-center slow-zoom"
            onError={() => setVideoFailed(true)}
            onLoadedData={() => setVideoFailed(false)}
          >
            <source
              src="https://www.wind-service0120.com/wp-content/uploads/2026/03/daikudouga-.mp4"
              type="video/mp4"
            />
          </video>
        </div>

        {/* Fallback image when video fails */}
        <div className={`absolute inset-0 ${videoFailed ? '' : 'hidden'}`}>
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20modern%20Japanese%20apartment%20living%20room%20after%20professional%20interior%20renovation%2C%20sparkling%20clean%20polished%20light%20wooden%20floor%20reflecting%20soft%20golden%20afternoon%20sunlight%20through%20large%20windows%2C%20fresh%20cream%20beige%20walls%20with%20subtle%20smooth%20texture%2C%20minimalist%20elegant%20interior%20design%2C%20spacious%20empty%20room%20ready%20for%20new%20tenant%20move-in%2C%20warm%20neutral%20earth%20tones%2C%20no%20people%2C%20high-end%20architectural%20interior%20photography%2C%20serene%20and%20inviting%20atmosphere%2C%20natural%20diffused%20daylight%2C%20pristine%20move-in%20condition%2C%20soft%20shadows%20across%20the%20floor&width=1600&height=900&seq=hero-poster-wind-2026-v2&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-center slow-zoom"
          />
        </div>

        {/* Overlay for contrast */}
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/55 via-foreground-950/35 to-foreground-950/65"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/40 via-transparent to-transparent"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full w-full flex flex-col justify-center px-6 md:px-16 lg:px-24">
        <div className="max-w-4xl w-full">
          <div className="fade-up inline-flex items-center gap-3 px-4 py-2 rounded-full bg-background-50/15 backdrop-blur-md border border-background-50/25 mb-6 md:mb-8">
            <span className="w-2 h-2 rounded-full bg-primary-400 flex items-center justify-center">
              <span className="w-2 h-2 rounded-full bg-primary-400 animate-ping"></span>
            </span>
            <span className="text-background-50 text-xs md:text-sm tracking-wider font-medium">
              東京都・神奈川県を中心に対応中
            </span>
          </div>

          <h1 className="fade-up-delay-1 font-heading text-background-50 leading-[1.2] mb-4 md:mb-6">
            <span className="block text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold">
              住まいに、
            </span>
            <span className="block text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold">
              <span className="text-primary-300">新しい風</span>を。
            </span>
          </h1>

          <p className="fade-up-delay-2 text-background-50/90 text-base md:text-xl leading-relaxed max-w-2xl mb-8 md:mb-12">
            地域密着のスピードと、職人の丁寧な仕上げで。<br className="hidden sm:block" />
            原状回復からリノベーションまで、住まいの価値を最大化します。
          </p>

          <div className="fade-up-delay-3 flex flex-col sm:flex-row gap-3 md:gap-4">
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-semibold tracking-wide transition-colors cursor-pointer whitespace-nowrap"
            >
              無料お見積もり
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="#services"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-background-50/10 hover:bg-background-50/20 backdrop-blur-md border border-background-50/30 text-background-50 font-semibold tracking-wide transition-colors cursor-pointer whitespace-nowrap"
            >
              事業内容を見る
              <i className="ri-arrow-down-line"></i>
            </a>
          </div>
        </div>

        {/* Bottom stats strip */}
        <div className="fade-up-delay-3 absolute bottom-8 md:bottom-12 left-6 md:left-16 lg:left-24 right-6 md:right-16 lg:right-24">
          <div className="flex flex-wrap items-end justify-between gap-6 pt-6 border-t border-background-50/25">
            <div className="text-background-50/80">
              <p className="text-[10px] md:text-xs tracking-[0.3em] uppercase mb-1">Our Story</p>
              <p className="text-sm md:text-base font-heading text-background-50">株式会社Wind</p>
            </div>
            <div className="flex items-center gap-6 md:gap-10">
              <div className="text-background-50">
                <div className="text-2xl md:text-4xl font-heading font-bold">2,310<span className="text-base md:text-xl">+</span></div>
                <div className="text-[10px] md:text-xs text-background-50/70 tracking-wider">施工実績</div>
              </div>
              <div className="w-px h-10 md:h-12 bg-background-50/30"></div>
              <div className="text-background-50">
                <div className="text-2xl md:text-4xl font-heading font-bold">98<span className="text-base md:text-xl">%</span></div>
                <div className="text-[10px] md:text-xs text-background-50/70 tracking-wider">お客様満足度</div>
              </div>
              <div className="hidden sm:block w-px h-10 md:h-12 bg-background-50/30"></div>
              <div className="hidden sm:block text-background-50">
                <div className="text-2xl md:text-4xl font-heading font-bold">24<span className="text-base md:text-xl">h</span></div>
                <div className="text-[10px] md:text-xs text-background-50/70 tracking-wider">最短対応</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}